import { Component, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { SimpleUserUpdate, UserCreate } from '../../../model/user.model';
import { ToolbarService } from '../../../services/toolbar.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { RepairerAdminService } from '../../../services/repairer-admin.service';

import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MultiLocalAdminService } from '../../../services/ml-admin.service';
import { PhoneValidator } from '../../../validators/phone.validator';
import { getErrorMessage } from '../../../consts/form-field-errors';

@Component({
  imports: [
    MatIconModule,
    MatDividerModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatCheckboxModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule
  ],
  selector: 'app-add-user',
  templateUrl: './add-user.component.html'
})
export class AddUserComponent implements OnInit {
  addUserForm: UntypedFormGroup;
  pending = signal<boolean>(false);
  protected readonly getErrorMessage = getErrorMessage;

  dialogRef = inject(MatDialogRef<AddUserComponent>);
  data = inject(MAT_DIALOG_DATA);
  private fb = inject(UntypedFormBuilder);
  public toolbarService = inject(ToolbarService);
  private message = inject(MessagingService);
  private escapeDialogRef = inject(EscapeDialogHelper);
  private mlAdminService = inject(MultiLocalAdminService);
  private repAdminService = inject(RepairerAdminService);

  get username() {
    return this.addUserForm.get('username');
  }
  get firstName() {
    return this.addUserForm.get('firstName');
  }
  get lastName() {
    return this.addUserForm.get('lastName');
  }
  get email() {
    return this.addUserForm.get('email');
  }
  get phone() {
    return this.addUserForm.get('phone');
  }
  get permission() {
    return this.addUserForm.get('permission');
  }
  get primaryContact() {
    return this.addUserForm.get('primaryContact');
  }

  ngOnInit() {
    this.addUserForm = this.fb.group({
      username: [this.data && this.data.user ? this.data.user.username : '', [Validators.required]],
      firstName: [this.data && this.data.user ? this.data.user.firstName : '', [Validators.required]],
      lastName: [this.data && this.data.user ? this.data.user.lastName : '', [Validators.required]],
      email: [
        this.data && this.data.user ? this.data.user.email : '',
        [
          Validators.required,
          Validators.pattern(
            '^([^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+\\.)*[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]' +
              '+@[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+(\\.[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+)+$'
          )
        ]
      ],
      phone: [this.data && this.data.user ? this.data.user.phone : '', PhoneValidator],
      permission: {
        value: this.data?.user?.privileges.includes('REPADMIN') ? 'REPADMIN' : 'User',
        disabled: this.data?.user?.privileges.includes('MLADMIN')
      },
      primaryContact: {
        value: this.data?.user?.isPrimaryContact ?? false,
        disabled: this.data?.user?.privileges.includes('MLADMIN')
      }
    });

    this.phone.markAsTouched();

    this.escapeDialogRef.escapeDialog(this.dialogRef);

    if (this.data?.user) {
      this.username.disable();
    }
  }

  saveUpdateUser() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');

    const user: UserCreate = {
      username: this.username.value.trim(),
      firstName: this.firstName.value.trim(),
      lastName: this.lastName.value.trim(),
      email: this.email.value.trim(),
      phone: this.phone.value.trim(),
      setAsContact: this.primaryContact.value
    };

    if (this.data?.user?.privileges.includes('MLADMIN')) {
      this.editMlAdminUser(user);
    } else {
      if (this.permission.value === 'REPADMIN') {
        user.privilege = this.permission.value;
      }
      if (!this.data?.user) {
        this.repAdminService.createUser(user).subscribe(
          () => this.dialogRef.close({ action: 'add', response: 'Success' }),
          (error) => {
            if (error.error === 'DUPLICATE_USERNAME') {
              this.username.setErrors({ usernameDuplicate: true });
            } else {
              this.message.error('Could not create the user.');
            }
            this.toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          },
          () => {
            this.toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          }
        );
      } else {
        this.repAdminService.updateSimpleUser(user, this.data.user.username).subscribe(
          () => this.dialogRef.close({ action: 'edit', response: 'Success' }),
          () => {
            this.message.error('Could not update user.');
            this.toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          },
          () => {
            this.toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          }
        );
      }
    }
  }

  private editMlAdminUser(user: SimpleUserUpdate) {
    this.mlAdminService.updateMlAdmin(this.data.user.username, user, [], false).subscribe(() => {
      this.dialogRef.close({ action: 'edit', response: 'Success' });
    });
  }
}
